<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import type FlightSchoolCourse from '@/types/FlightSchoolCourse';
import type {
    StudentCourseStatus,
    FlightSchoolCourseStudent,
    StudentCourseLockout,
    StudentExamStatus,
} from '@/types/FlightSchoolStudent';
import {
    type PracticePerformance,
    type StagePerformance,
    type StudentRatingPerformance,
    type StudyUnitPerformance,
    quizPerformanceToGradeString,
} from '@/types/Performance';
import {
    type Rating,
    extractRatingFromPerformances,
    type Stage,
    type StudyUnit,
} from '@/types/CourseContent';
import StudentLinkTable from './StudentLinkTable.vue';
import type { TypedColumns } from '@/types/QuasarOverrides';
import type FlightSchool from '@/types/FlightSchool';
import CoursePerformanceRepository from '@/repositories/CoursePerformanceRepository';
import { numericSortCriteria } from '@/functions/sortCriteria';
import StudentRepository from '@/repositories/StudentRepository';

// Maybe define exactly what we want in this row for better typechecking.
type PerformanceRow = {
    [key: string]: string|number|boolean|object|null;
} & {
    isFocused: boolean;
    id: number;
    name: string;
    courseStatus: StudentCourseStatus;
    examStatus: StudentExamStatus;
    lockout: StudentCourseLockout | null;
    flightSchool: FlightSchool;
    courseAcronym: string;
    ratingAcronym: string;
    part: number;
    enrollDate: Date;
    examDate: Date | null;
    gradDate: Date | null;
    email: string;
};

const props = defineProps<{
    currentSchool: FlightSchool,
    currentCourse: FlightSchoolCourse
    currentRatingUuid: string,
}>();

const getRowFromPerformance = (performance: StudentRatingPerformance): PerformanceRow => {
    const studentRow: PerformanceRow = {
        id: performance.student.id,
        name: `${performance.student.firstName} ${performance.student.lastName}`,
        eoc: quizPerformanceToGradeString(performance.eocPerformance),
        courseStatus: performance.student.courseStatus,
        examStatus: performance.student.examStatus,
        lockout: performance.student.lockout,
        isFocused: false,
        flightSchool: performance.student.flightSchool,
        courseAcronym: performance.student.courseAcronym,
        ratingAcronym: performance.student.ratingAcronym,
        part: performance.student.part,
        enrollDate: performance.student.enrollDate,
        gradDate: performance.student.gradDate,
        examDate: performance.student.examDate,
        email: performance.student.email,
    };

    performance.studyUnitPerformance.forEach(({
        studyUnitNumber,
        finalQuizPerformance,
        optionalQuizPerformance,
    }: StudyUnitPerformance) => {
        const suKey = `su${studyUnitNumber}`;
        const finalQuizString = quizPerformanceToGradeString(finalQuizPerformance);
        const optionalQuiz = quizPerformanceToGradeString(optionalQuizPerformance);
        const optionalQuizString = `${optionalQuiz === '--' ? '' : `, ${optionalQuiz}`}`;
        studentRow[suKey] = `${finalQuizString}${optionalQuizString}`;
    });

    performance.stagePerformance.forEach((stagePerf: StagePerformance) => {
        const stageKey = `stage${stagePerf.stageNumber.toString()}`;
        studentRow[stageKey] = quizPerformanceToGradeString(stagePerf);
    });

    performance.practicePerformance.forEach((
        practicePerformance: PracticePerformance,
        practiceIndex: number,
    ) => {
        const testNumber = practiceIndex + 1;

        const pracKey = testNumber > 2
            ? `Bonus${(testNumber - 2).toString()}`
            : `Practice${testNumber.toString()}`;

        studentRow[pracKey] = quizPerformanceToGradeString(practicePerformance);
    });

    return studentRow;
};

const loading = ref<boolean>(true);
const courseStudents = ref<FlightSchoolCourseStudent[]>([]);
const studentPerformance = ref<StudentRatingPerformance[]>([]);
const rows = ref<PerformanceRow[]>([]);
const columns = ref<TypedColumns>([]);
const ratingContent = ref<Rating>({
    stages: [],
});

const buildContentColumns = (rating: Rating, typedColumns: TypedColumns): TypedColumns => {
    rating.stages.forEach((stage: Stage) => {
        stage.studyUnits.forEach((studyUnit: StudyUnit) => {
            const suKey = `su${studyUnit.studyUnitNumber}`;
            typedColumns?.push({
                name: suKey,
                label: `SU ${studyUnit.studyUnitNumber}`,
                sortable: true,
                sort: numericSortCriteria,
                field: suKey,
                align: 'left' as const,
            });
        });

        const stageKey = `stage${stage.stageNumber}`;
        typedColumns?.push({
            name: stageKey,
            label: `Stage ${stage.stageNumber}`,
            sortable: true,
            sort: numericSortCriteria,
            field: stageKey,
            align: 'left' as const,
        });
    });

    return typedColumns;
};

const getPracticeCols = (): TypedColumns => {
    const practiceColumns: TypedColumns = [];

    for (let practiceNumber = 1; practiceNumber <= 5; practiceNumber += 1) {
        const isBonus = practiceNumber > 2;
        const testNumber = isBonus ? practiceNumber - 2 : practiceNumber;
        const testName = isBonus ? `Bonus${testNumber}` : `Practice${testNumber}`;
        const label = `${testName.charAt(0)}${testNumber}`;

        practiceColumns.push({
            name: testName,
            label,
            sortable: true,
            sort: numericSortCriteria,
            field: testName,
            align: 'left' as const,
        });
    }

    return practiceColumns;
};

const setupTable = (studentPerformances: StudentRatingPerformance[]) => {
    // See comments in CourseContent
    let ratingColumns: TypedColumns = [
        {
            name: 'name', label: 'Name', sortable: true, field: 'name', align: 'left' as const,
        },
    ];

    if (studentPerformances.length > 0) {
        ratingContent.value = extractRatingFromPerformances(studentPerformances[0]);

        rows.value = studentPerformances.map(
            (performance: StudentRatingPerformance) => getRowFromPerformance(performance),
        );

        ratingColumns = buildContentColumns(ratingContent.value, ratingColumns);
    }

    columns.value = ratingColumns.concat([
        {
            name: 'eoc', label: 'EOC Test', sortable: true, sort: numericSortCriteria, field: 'eoc', align: 'left' as const,
        },
        ...getPracticeCols(),
        {
            name: 'courseStatus', label: 'Course Status', sortable: true, field: 'courseStatus', align: 'left' as const,
        },
        {
            name: 'examStatus', label: 'Exam Status', sortable: true, field: 'examStatus', align: 'left' as const,
        },
    ]);
};

const loadCourseStudents = async () => {
    courseStudents.value = await StudentRepository.getCourseStudentsByRatingUuid(
        props.currentSchool,
        props.currentCourse,
        props.currentRatingUuid,
    );

    /**
     * There is the potential to have more than 100 students within a course.
     * Given that fact, chunk the requetss into groups of 5 to 15 students
     * to avoid overloading the server with requets. The remainder of the
     * students will be loaded in the background.
     */
    const studentChunks = courseStudents.value.reduce((students: FlightSchoolCourseStudent[][], student) => {
        if (students[students.length - 1].length < 15) {
            students[students.length - 1].push(student);
        } else {
            students.push([student]);
        }

        return students;
    }, [[]]);

    // load the first page of students immediately
    studentPerformance.value = await Promise.allSettled(
        studentChunks[0].map((student: FlightSchoolCourseStudent) => CoursePerformanceRepository
            .getStudentRatingPerformance(props.currentSchool, props.currentCourse, student)),
    ).then((performanceData) => performanceData
        .filter((promise): promise is PromiseFulfilledResult<StudentRatingPerformance> => promise.status === 'fulfilled')
        .map((promise) => promise.value));

    setupTable(studentPerformance.value);

    // load the rest of the students in the background
    for (let i = 1; i < studentChunks.length; i += 1) {
        /* eslint-disable-next-line no-await-in-loop */
        await Promise.allSettled(
            studentChunks[i].map((student: FlightSchoolCourseStudent) => CoursePerformanceRepository
                .getStudentRatingPerformance(props.currentSchool, props.currentCourse, student)),
        ).then((performanceData) => {
            const newPerformances = performanceData
                .filter((promise): promise is PromiseFulfilledResult<StudentRatingPerformance> => promise.status === 'fulfilled')
                .map((promise) => promise.value);

            studentPerformance.value = studentPerformance.value.concat(newPerformances);

            setupTable(studentPerformance.value);
        });
    }
};

onMounted(async () => {
    await loadCourseStudents();
    loading.value = false;
});

/**
 * watch for changes to the current school and course
 * when either changes, we need to reload the data
 */
watch(() => [props.currentSchool, props.currentCourse], async () => {
    loading.value = true;
    // check admin access to school and course here?
    rows.value = [];
    await loadCourseStudents();
    loading.value = false;
});
</script>

<template>
    <student-link-table
        :rows="rows"
        :columns="columns"
        :loading="loading"
    />
</template>
